import AppContext from '@/AppContext';
import { EntityList } from '@/components/EntityList';
import { ListColumn } from '@/components/ListLayout';
import {
  getFontAwesomeIconForFileExtension,
  getStatusFromOnboardingUploadEmployee,
} from '@/lib/utils';
import { Form, Grid, Pagination } from '@/semantic-ui/components';
import { useStores } from '@/store';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { debounce } from 'lodash';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useCallback, useContext, useState } from 'react';
import { useSearchParams } from 'react-router';
import { OnboardingDocumentEmployee, toNum } from 'sol-data';
import PositionFilterDropdown from '../PositionFilterDropdown';

const RESULTS_PER_PAGE = 10;
const DEBOUNCE_DELAY = 200;

export const OnboardingDocumentSubmissionsList = observer(() => {
  const { onboardingDocumentEmployeeStore } = useStores();
  const [onboardingDocumentEmployees, setOnboardingDocumentEmployees] = useState<
    OnboardingDocumentEmployee[]
  >([]);

  const [currentPage, setCurrentPage] = useState(1);
  const appContext = useContext(AppContext);
  const isAdmin = appContext?.isAdmin;
  const [searchParams, setSearchParams] = useSearchParams();
  const page = searchParams.get('page') ? Number(searchParams.get('page')) : 1;
  const positionId = searchParams.get('position')
    ? Number(searchParams.get('position'))
    : undefined;
  const [search, setSearch] = useState('');

  const fetchData = async (pageNumber: number, searchValue?: string, positionIdValue?: number) => {
    onboardingDocumentEmployeeStore.clearCurrent();

    const data = await onboardingDocumentEmployeeStore.fetchAllPaginated(
      pageNumber,
      searchValue,
      positionIdValue,
    );

    setCurrentPage(pageNumber);
    setOnboardingDocumentEmployees(data);
  };

  const changePage = async (pageNumber: number, searchValue?: string, positionIdValue?: number) => {
    onboardingDocumentEmployeeStore.setPage(pageNumber);
    searchParams.set('page', pageNumber.toString());
    searchParams.set('position', positionIdValue?.toString() ?? '');
    setSearchParams(searchParams);

    await fetchData(pageNumber, searchValue, positionIdValue);
  };

  const handlePositionChange = async (value: number) => {
    setSearch('');

    await changePage(1, '', value);
  };

  const updateSearch = useCallback(
    debounce(async (searchValue: string) => {
      await changePage(1, searchValue, undefined);
    }, DEBOUNCE_DELAY),
    [page, positionId],
  );

  const { isLoading, errorMessage } = onboardingDocumentEmployeeStore;

  const columns: ListColumn<OnboardingDocumentEmployee>[] = [
    {
      name: 'Title',
      key: 'title',
      width: { sm: 4, lg: 4 },
      render: ({ onboardingDocument }) => <span>{onboardingDocument?.title}</span>,
    },
    {
      name: 'Employee',
      key: 'employee',
      width: { sm: 2, lg: 2 },
      textAlign: 'center',
      render: ({ user }) => {
        return (
          <div
            style={{
              textAlign: 'center',
            }}
          >
            {user?.fullName}
          </div>
        );
      },
    },
    {
      name: 'Submitted On',
      key: 'submittedOn',
      width: { sm: 2, lg: 2 },
      textAlign: 'center',
      render: ({ submittedOn }) => (
        <div
          style={{
            textAlign: 'center',
          }}
        >
          {submittedOn}
        </div>
      ),
    },
    {
      name: 'Status',
      key: 'status',
      width: { sm: 2, lg: 2 },
      textAlign: 'center',
      render: (documentEmployee) => {
        return (
          <div style={{ textAlign: 'center' }}>
            {getStatusFromOnboardingUploadEmployee(documentEmployee)}
          </div>
        );
      },
    },
    {
      name: 'File Name',
      key: 'fileName',
      width: { sm: 3, lg: 3 },
      render: ({ onboardingDocument }) => (
        <div style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
          <FontAwesomeIcon
            icon={[
              'far',
              getFontAwesomeIconForFileExtension(onboardingDocument?.fileName?.split('.').pop()!),
            ]}
            className="reveal-icon"
            style={{ marginRight: 4 }}
          />
          {onboardingDocument?.fileName}
        </div>
      ),
    },
  ];

  return (
    <>
      <EntityList<OnboardingDocumentEmployee>
        title="Onboarding Documents"
        searchLabel="Search by document, file name or employee"
        columns={columns}
        items={onboardingDocumentEmployees}
        newLink="/onboarding/documents/templates/add"
        titleProp="employee"
        searchValue={search}
        onSearchChange={(filter) => {
          setSearch(filter);
          updateSearch(filter);
        }}
        onDismiss={() => onboardingDocumentEmployeeStore.clearError()}
        onRefresh={() => fetchData(page, search, positionId)}
        errorMessage={errorMessage}
        isLoading={isLoading}
        noNewButton={!isAdmin}
        searchHeaderContent={
          <Form>
            <Form.Field>
              <PositionFilterDropdown
                limited
                clearable
                name="position"
                placeholder="Filter by position"
                positionId={positionId ? toNum(positionId) : undefined}
                onChange={(_, value) => handlePositionChange(value)}
              />
            </Form.Field>
          </Form>
        }
      />
      <Grid centered>
        <Grid.Row>
          <Pagination
            activePage={currentPage}
            totalPages={Math.ceil(
              onboardingDocumentEmployeeStore.allEmployeesCount / RESULTS_PER_PAGE,
            )}
            onPageChange={(_, { activePage }) => {
              changePage(activePage as number, search, positionId);
            }}
            siblingRange={2}
            boundaryRange={0}
            firstItem={{ content: 'First' }}
            lastItem={{ content: 'Last' }}
            ellipsisItem={null}
            prevItem={null}
            nextItem={null}
          />
        </Grid.Row>
      </Grid>
    </>
  );
});
